import { IVoucher } from '@model';
import { dateInRange, getTodayDate } from '@utils/date';

import { EBenefitState, IBenefits } from '../model';

import { getDeliveryBenefit } from './benefit';

export const hasFreeDeliveryVoucher = (vouchers: IVoucher[]): boolean =>
  vouchers.some((voucher) => voucher.isDeliveryVoucher);

/**
 * Method checks whether the user has active FreeDeliveryBenefit
 * @param benefits - Customer benefits
 */
export const hasActiveFreeDeliveryBenefit = (benefits: IBenefits): boolean => {
  const freeDeliveryBenefit = getDeliveryBenefit(benefits.loyalties);

  return freeDeliveryBenefit?.state === EBenefitState.ACTIVE;
};

/**
 * This function returns if user has some delivery for free
 *
 * Free delivery is available if feature switch for free delivery is enabled
 *    AND current date is in feature switch set date
 *    AND order summary price is in free delivery global limit
 * @return {true}
 *
 * OR
 *
 * Free delivery is available if customer has free delivery benefit or free delivery voucher. @return {true}
 *
 * Otherwise free delivery is not available (@return {false})
 *
 * @param {boolean} freeDelivery - Free delivery feature is enabled
 * @param {Date} freeDeliveryGlobalFrom - Free delivery feature enable from
 * @param {Date} freeDeliveryGlobalTo - Free delivery feature enable to
 * @param {number} freeDeliveryGlobalLimit - Free delivery global limit
 * @param {number} priceProductsSummary - Cart price item summary
 * @param {number} discountWithoutDelivery - Discount value without delivery
 * @param {IBenefits} benefits  - benefits
 * @returns {boolean}
 */
export const isFreeDeliveryAvailable = (
  freeDelivery: boolean,
  freeDeliveryGlobalFrom: Date,
  freeDeliveryGlobalTo: Date,
  freeDeliveryGlobalLimit: number,
  priceProductsSummary: number,
  discountWithoutDelivery: number,
  benefits: IBenefits,
): boolean =>
  /* Feature switch settings check */
  (freeDelivery &&
    dateInRange(getTodayDate(), freeDeliveryGlobalFrom, freeDeliveryGlobalTo) &&
    priceProductsSummary - discountWithoutDelivery >= freeDeliveryGlobalLimit) ||
  /* Vouchers check */
  hasFreeDeliveryVoucher(benefits.vouchers) ||
  /* Benefits check */
  hasActiveFreeDeliveryBenefit(benefits);
